import { RUNTIME_TOOL_DISPLAY_IDENTITY_MAX_CHARS } from "@t3tools/contracts";

export type ActivityPresentationClassification = "omit" | "groupable" | "standalone";

export interface ActivityToolDisplayIdentity {
  readonly server?: string | undefined;
  readonly tool?: string | undefined;
  readonly appName?: string | undefined;
  readonly actionName?: string | undefined;
}

export interface ActivityToolFacts {
  readonly operationKind: "mcp" | "computerUse";
  readonly appUi: boolean;
  readonly displayIdentity?: ActivityToolDisplayIdentity | undefined;
}

export interface ActivityToolDisplayPresentation {
  readonly title: string;
  readonly detail?: string | undefined;
}

export interface ActivityPresentationCandidate {
  readonly id: string;
  readonly identity?: string | undefined;
  /** Existing, provider-authored action title. Never derive this from output text. */
  readonly actionTitle?: string | undefined;
  readonly turnId?: string | null | undefined;
  readonly activityKind?: string | undefined;
  readonly itemType?: string | undefined;
  readonly lifecycleStatus?:
    | "inProgress"
    | "completed"
    | "failed"
    | "declined"
    | "stopped"
    | undefined;
  readonly requestKind?: string | undefined;
  readonly tone?: string | undefined;
  readonly toolFacts?: ActivityToolFacts | undefined;
  /** Bounded canonical command supplied by the client normalization boundary. */
  readonly command?: string | undefined;
  /** Bounded canonical paths collected at the client normalization boundary. */
  readonly changedFiles?: ReadonlyArray<string> | undefined;
  /** Bounded canonical query facts, when the provider supplies them explicitly. */
  readonly webSearchFacts?: ReadonlyArray<string> | undefined;
  readonly webSearchHasContent?: boolean | undefined;
}

export type ActivityGroupSummaryPartKey =
  | "mcp-tools"
  | "read-files"
  | "file-searches"
  | "changed-files"
  | "commands"
  | "web-searches"
  | "other-tools";

export interface ActivityGroupSummaryPart {
  readonly key: ActivityGroupSummaryPartKey;
  readonly count: number;
}

export type ActivityGroupSummary =
  | {
      readonly kind: "single-action";
      readonly title: string;
    }
  | {
      readonly kind: "parts";
      readonly parts: ReadonlyArray<ActivityGroupSummaryPart>;
    };

export type ActivityGroupCurrentAction =
  | {
      readonly kind: "tool";
      readonly category: "command" | "file-read" | "file-search" | "file-change" | "web-search";
      readonly detail?: string | undefined;
    }
  | {
      readonly kind: "action";
      readonly title: string;
      readonly detail?: string | undefined;
    }
  | {
      readonly kind: "fallback";
    };

export type ActivityPresentationUnit<T> =
  | {
      readonly kind: "group";
      readonly id: string;
      readonly turnId: string | null;
      readonly activities: ReadonlyArray<T>;
    }
  | {
      readonly kind: "standalone";
      readonly id: string;
      readonly turnId: string | null;
      readonly activity: T;
    };

const OMITTED_ACTIVITY_KINDS = new Set([
  "checkpoint",
  "context-window.updated",
  "task.started",
  "tool.started",
  "turn.plan.updated",
]);

const ACTIVITY_CURRENT_ACTION_TITLE_MAX_CHARS = 160;
const ACTIVITY_CURRENT_ACTION_DETAIL_MAX_CHARS = 512;

function isGroupableToolItemType(itemType: string | undefined): boolean {
  return (
    itemType === "command_execution" ||
    itemType === "file_read" ||
    itemType === "file_search" ||
    itemType === "file_change" ||
    itemType === "mcp_tool_call" ||
    itemType === "dynamic_tool_call" ||
    itemType === "web_search"
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function boundedDisplayIdentity(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= RUNTIME_TOOL_DISPLAY_IDENTITY_MAX_CHARS
    ? trimmed
    : undefined;
}

export function parseActivityToolFacts(value: unknown): ActivityToolFacts | undefined {
  const facts = asRecord(value);
  if (
    !facts ||
    (facts.operationKind !== "mcp" && facts.operationKind !== "computerUse") ||
    typeof facts.appUi !== "boolean"
  ) {
    return undefined;
  }

  const rawIdentity = asRecord(facts.displayIdentity);
  const server = boundedDisplayIdentity(rawIdentity?.server);
  const tool = boundedDisplayIdentity(rawIdentity?.tool);
  const appName = boundedDisplayIdentity(rawIdentity?.appName);
  const actionName = boundedDisplayIdentity(rawIdentity?.actionName);
  const displayIdentity = {
    ...(server ? { server } : {}),
    ...(tool ? { tool } : {}),
    ...(appName ? { appName } : {}),
    ...(actionName ? { actionName } : {}),
  };

  return {
    operationKind: facts.operationKind,
    appUi: facts.appUi,
    ...(Object.keys(displayIdentity).length > 0 ? { displayIdentity } : {}),
  };
}

export function activityToolFactsRequireStandalone(
  toolFacts: ActivityToolFacts | undefined,
): boolean {
  return toolFacts?.operationKind === "computerUse" || toolFacts?.appUi === true;
}

export function resolveActivityToolDisplayPresentation(
  toolFacts: ActivityToolFacts | undefined,
): ActivityToolDisplayPresentation | null {
  const identity = toolFacts?.displayIdentity;
  if (!identity) {
    return null;
  }

  const values: string[] = [];
  for (const value of [identity.actionName, identity.appName, identity.tool, identity.server]) {
    if (value && !values.includes(value)) {
      values.push(value);
    }
  }
  const title = values[0];
  if (!title) {
    return null;
  }
  return {
    title,
    ...(values[1] ? { detail: values[1] } : {}),
  };
}

function isApprovalOrInputActivity(kind: string | undefined): boolean {
  return (
    kind?.startsWith("approval.") === true ||
    kind?.startsWith("provider.approval.") === true ||
    kind?.startsWith("user-input.") === true
  );
}

export function classifyActivity(
  activity: ActivityPresentationCandidate,
): ActivityPresentationClassification {
  if (
    activity.tone === "error" ||
    activity.activityKind === "runtime.error" ||
    activity.activityKind === "runtime.warning" ||
    activity.requestKind !== undefined ||
    isApprovalOrInputActivity(activity.activityKind) ||
    activity.lifecycleStatus === "failed" ||
    activity.lifecycleStatus === "declined" ||
    activity.lifecycleStatus === "stopped"
  ) {
    return "standalone";
  }
  if (activityToolFactsRequireStandalone(activity.toolFacts)) {
    return "standalone";
  }
  if (activity.activityKind && OMITTED_ACTIVITY_KINDS.has(activity.activityKind)) {
    const isRunningKnownToolStart =
      activity.activityKind === "tool.started" &&
      activity.lifecycleStatus === "inProgress" &&
      isGroupableToolItemType(activity.itemType);
    if (!isRunningKnownToolStart) {
      return "omit";
    }
  }

  switch (activity.itemType) {
    case "command_execution":
    case "file_read":
    case "file_search":
    case "file_change":
    case "mcp_tool_call":
    case "dynamic_tool_call":
      return "groupable";
    case "web_search":
      return activity.lifecycleStatus === "inProgress" || activity.webSearchHasContent === true
        ? "groupable"
        : "omit";
    case "image_view":
    case "collab_agent_tool_call":
      return "standalone";
    default:
      return "standalone";
  }
}

function groupIdentity(activity: ActivityPresentationCandidate): string {
  return activity.identity?.trim() || activity.id;
}

function boundedTitle(value: string | undefined): string | null {
  if (value === undefined || value.length > ACTIVITY_CURRENT_ACTION_TITLE_MAX_CHARS) {
    return null;
  }
  const title = value.trim();
  return title.length > 0 ? title : null;
}

function boundedDetail(value: string | undefined): string | null {
  if (value === undefined || value.length > ACTIVITY_CURRENT_ACTION_DETAIL_MAX_CHARS) {
    return null;
  }
  const detail = value.normalize("NFC").replace(/\s+/g, " ").trim();
  return detail.length > 0 ? detail : null;
}

function distinctDetail(title: string, value: string | null): string | undefined {
  return value && value !== title ? value : undefined;
}

function resolveActivityGroupCurrentAction(
  candidate: ActivityPresentationCandidate,
): ActivityGroupCurrentAction {
  const providerTitle = boundedTitle(candidate.actionTitle);

  if (candidate.itemType === "command_execution") {
    const detail = boundedDetail(candidate.command);
    return { kind: "tool", category: "command", ...(detail ? { detail } : {}) };
  }
  if (candidate.itemType === "file_read") {
    return { kind: "tool", category: "file-read" };
  }
  if (candidate.itemType === "file_search") {
    return { kind: "tool", category: "file-search" };
  }
  if (candidate.itemType === "file_change") {
    const detail = boundedDetail(candidate.changedFiles?.[0]);
    return { kind: "tool", category: "file-change", ...(detail ? { detail } : {}) };
  }
  if (candidate.itemType === "web_search") {
    const detail = boundedDetail(candidate.webSearchFacts?.[0]);
    return { kind: "tool", category: "web-search", ...(detail ? { detail } : {}) };
  }

  if (candidate.itemType === "mcp_tool_call") {
    const toolPresentation = resolveActivityToolDisplayPresentation(candidate.toolFacts);
    const title = boundedTitle(toolPresentation?.title) ?? providerTitle;
    if (!title) {
      return { kind: "fallback" };
    }
    const detail = distinctDetail(title, boundedDetail(toolPresentation?.detail));
    return { kind: "action", title, ...(detail ? { detail } : {}) };
  }

  if (!providerTitle) {
    return { kind: "fallback" };
  }

  return { kind: "action", title: providerTitle };
}

function canonicalFact(value: string): string | null {
  if (value.length > 512) {
    return null;
  }
  const fact = value.normalize("NFC").trim();
  return fact.length > 0 ? fact : null;
}

/**
 * Chooses the latest in-progress groupable activity. A later unknown action
 * intentionally yields the fallback instead of reviving a stale earlier title.
 */
export function selectActivityGroupCurrentAction<T>(
  activities: ReadonlyArray<T>,
  toCandidate: (activity: T) => ActivityPresentationCandidate = (activity) =>
    activity as ActivityPresentationCandidate,
): ActivityGroupCurrentAction | null {
  for (let index = activities.length - 1; index >= 0; index -= 1) {
    const activity = activities[index];
    if (activity === undefined) {
      continue;
    }
    const candidate = toCandidate(activity);
    if (candidate.lifecycleStatus !== "inProgress" || classifyActivity(candidate) !== "groupable") {
      continue;
    }
    return resolveActivityGroupCurrentAction(candidate);
  }
  return null;
}

/**
 * Aggregates only bounded facts supplied by the normalization boundary. In
 * particular, candidates deliberately have no detail, raw output, or MCP
 * response fields for this function to inspect.
 */
export function summarizeActivityGroup<T>(
  activities: ReadonlyArray<T>,
  toCandidate: (activity: T) => ActivityPresentationCandidate = (activity) =>
    activity as ActivityPresentationCandidate,
): ActivityGroupSummary | null {
  const uniqueCandidates: ActivityPresentationCandidate[] = [];
  const seenIdentities = new Set<string>();

  for (const activity of activities) {
    const candidate = toCandidate(activity);
    if (classifyActivity(candidate) !== "groupable") {
      continue;
    }
    const identity = groupIdentity(candidate);
    if (seenIdentities.has(identity)) {
      continue;
    }
    seenIdentities.add(identity);
    uniqueCandidates.push(candidate);
  }

  if (uniqueCandidates.length === 0) {
    return null;
  }
  if (uniqueCandidates.length === 1) {
    const title = boundedTitle(uniqueCandidates[0]?.actionTitle);
    if (title) {
      return { kind: "single-action", title };
    }
  }

  let mcpToolCount = 0;
  let fileReadCount = 0;
  let fileSearchCount = 0;
  let commandCount = 0;
  let otherToolCount = 0;
  const changedFiles = new Set<string>();
  const webSearches = new Set<string>();

  for (const candidate of uniqueCandidates) {
    switch (candidate.itemType) {
      case "mcp_tool_call":
        mcpToolCount += 1;
        break;
      case "file_read":
        fileReadCount += 1;
        break;
      case "file_search":
        fileSearchCount += 1;
        break;
      case "file_change": {
        let hasCanonicalPath = false;
        for (const path of candidate.changedFiles ?? []) {
          const canonicalPath = canonicalFact(path);
          if (canonicalPath) {
            hasCanonicalPath = true;
            changedFiles.add(canonicalPath);
          }
        }
        if (!hasCanonicalPath) {
          otherToolCount += 1;
        }
        break;
      }
      case "command_execution":
        commandCount += 1;
        break;
      case "web_search": {
        let hasCanonicalSearch = false;
        for (const fact of candidate.webSearchFacts ?? []) {
          const canonicalSearch = canonicalFact(fact);
          if (canonicalSearch) {
            hasCanonicalSearch = true;
            webSearches.add(`fact:${canonicalSearch}`);
          }
        }
        if (!hasCanonicalSearch) {
          webSearches.add(`activity:${groupIdentity(candidate)}`);
        }
        break;
      }
      case "dynamic_tool_call":
        otherToolCount += 1;
        break;
    }
  }

  const parts: ActivityGroupSummaryPart[] = [];
  if (mcpToolCount > 0) parts.push({ key: "mcp-tools", count: mcpToolCount });
  if (fileReadCount > 0) parts.push({ key: "read-files", count: fileReadCount });
  if (fileSearchCount > 0) parts.push({ key: "file-searches", count: fileSearchCount });
  if (changedFiles.size > 0) parts.push({ key: "changed-files", count: changedFiles.size });
  if (commandCount > 0) parts.push({ key: "commands", count: commandCount });
  if (webSearches.size > 0) parts.push({ key: "web-searches", count: webSearches.size });
  if (otherToolCount > 0) parts.push({ key: "other-tools", count: otherToolCount });

  return parts.length > 0 ? { kind: "parts", parts } : null;
}

/**
 * Groups an already lifecycle-collapsed run of client activities. Callers keep
 * messages and other timeline content as boundaries between runs.
 */
export function groupActivities<T>(
  activities: ReadonlyArray<T>,
  toCandidate: (activity: T) => ActivityPresentationCandidate = (activity) =>
    activity as ActivityPresentationCandidate,
): ActivityPresentationUnit<T>[] {
  const result: ActivityPresentationUnit<T>[] = [];
  let activeActivities: T[] = [];
  let activeTurnId: string | null = null;
  let previousTurnId: string | null | undefined;

  const flush = () => {
    const first = activeActivities[0];
    if (!first) {
      return;
    }
    result.push({
      kind: "group",
      id: `activity-group:${activeTurnId ?? "unscoped"}:${groupIdentity(toCandidate(first))}`,
      turnId: activeTurnId,
      activities: activeActivities,
    });
    activeActivities = [];
  };

  for (const activity of activities) {
    const candidate = toCandidate(activity);
    const turnId = candidate.turnId ?? null;
    if (previousTurnId !== undefined && previousTurnId !== turnId) {
      flush();
    }
    previousTurnId = turnId;

    const classification = classifyActivity(candidate);
    if (classification === "omit") {
      continue;
    }
    if (classification === "standalone") {
      flush();
      result.push({
        kind: "standalone",
        id: `standalone-activity:${groupIdentity(candidate)}`,
        turnId,
        activity,
      });
      continue;
    }
    if (activeActivities.length === 0) {
      activeTurnId = turnId;
    }
    activeActivities.push(activity);
  }

  flush();
  return result;
}
